import {
  LargeHeadingStyled,
  SmallHeadingStyled,
  SmallMessageStyled,
} from "../../../components/custom/texts/texts.styled";
import { Box, Button, Card, Drawer, Flex, Icon, Text } from "@nubeteck/components";
import { Dropdown, Form, Image, InputNumber, Layout, Modal, Select, Spin, Table, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { NoResultsSVG } from "src/assets";
import { useSuccessRequestCreatedToast, useSuccessRequestDeletedToast } from "src/constants";
import { toastErrorStyle } from "src/constants";
import { ArrayUtils } from "@nubeteck/utils";
import {
  useCreateRequestMutation,
  useGetFieldTypesQuery,
  useGetRequestByIdMutation,
  useGetRequestMessageMutation,
  useGetRequestsAdminMutation,
  useGetRequestsMutation,
  useUpdateRequestStateMutation,
} from "src/services/requestsServices";
import FormItem from "antd/es/form/FormItem";
import ConfigurationInput from "../../admin-pages/generalSettings/settingsInput";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/core/store/store";
import parse from "html-react-parser";
import { getAdmissionsMadePage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../../pages/StrapiError";
import { _translateValue } from "../../admin-pages/translation-gestor/translateValue";
import { formatDate } from "src/utils/date-util";

const { Content } = Layout;
const SCREEN_NAME = "PantallaRequests";

interface ISolicitud {
  solicitudId: number;
  estudianteSolicitudId: number;
  solicitudNombre: string;
  estadoId: number;
  estadoNombre: string;
  fecha: string;
}

const Requests: React.FC = () => {
  const [form] = Form.useForm<ISolicitud>();
  const [pageNumber, setPageNumber] = React.useState(1);
  const [rowsCount, setRowsCount] = React.useState(5);
  const { locale } = useLocale();
  const [admissionsMadePage, setAdmissionsMadePage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [modal, contextHolder] = Modal.useModal();
  const [translatedData, setTranslatedData] = React.useState<{
    solicitudes: { [key: string]: string };
    estados: { [key: string]: string };
    campos: { [key: string]: string };
    informacionesAdicionales: { [key: string]: string };
    descripciones: { [key: string]: string };
    opciones: { [key: string]: string };
  }>({
    solicitudes: {},
    estados: {},
    campos: {},
    informacionesAdicionales: {},
    descripciones: {},
    opciones: {},
  });
  const showSuccessRequestCreatedToast = useSuccessRequestCreatedToast();
  const showSuccessRequestDeletedToast = useSuccessRequestDeletedToast();

  const [getRequests, { data, isLoading }] = useGetRequestsMutation();
  const [getRequestById, { data: requestById, isLoading: isLoadingById }] =
    useGetRequestByIdMutation();
  const [getRequestMessage, { data: requestMessage, isLoading: isLoadingMessage }] =
    useGetRequestMessageMutation();
  const [createRequest, { isLoading: isLoadingCreate, isSuccess: isSuccessCreate }] =
    useCreateRequestMutation();
  const [deleteRequest, { isLoading: isLoadingDelete, isSuccess: isSuccessDelete }] =
    useUpdateRequestStateMutation();
  const [getConcepts, { data: conceptos }] = useGetRequestsAdminMutation();
  const { isATeacher } = useSelector((state: RootState) => state.user);

  //const [fechasFiltro, setFechasFiltro] = React.useState({ fechaInicio: null, fechaFin: null });
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const { data: tiposCampos } = useGetFieldTypesQuery();

  const navigate = useNavigate();

  const solicitudesConceptos = React.useMemo(() => {
    return (conceptos?.data ?? []).map((concepto: any) => ({
      label: translatedData.solicitudes[concepto.solicitudNombre] || concepto.solicitudNombre,
      value: concepto.solicitudId,
    }));
  }, [conceptos?.data, translatedData.solicitudes]);

  const obtenerSolicitudesRealizadas = useCallback(() => {
    getRequests({
      rowsCount,
      page: pageNumber,
    });
  }, [rowsCount, pageNumber, getRequests]);

  useEffect(() => {
    getConcepts();
    getRequestMessage();
  }, [getConcepts, getRequestMessage]);

  useEffect(() => {
    obtenerSolicitudesRealizadas();
  }, [isSuccessDelete, obtenerSolicitudesRealizadas]);

  useEffect(() => {
    if (isSuccessCreate) {
      showSuccessRequestCreatedToast();
      if (isATeacher || !requestById?.data?.requierePago) obtenerSolicitudesRealizadas();
      else navigate("/cart/requests");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessCreate, showSuccessRequestCreatedToast]);
  useEffect(() => {
    if (isSuccessDelete) {
      showSuccessRequestDeletedToast();
      obtenerSolicitudesRealizadas();
    }
  }, [isSuccessDelete, obtenerSolicitudesRealizadas, showSuccessRequestDeletedToast]);

  const submit = (data: any) => {
    const solicitudId: number = data.solicitudId;
    const cantidad: number = data.cantidad
      ? data.cantidad
      : requestById?.data?.requierePago
      ? 1
      : 0;
    delete data.solicitudId;
    delete data.cantidad;
    const newData = {
      cantidad,
      fecha: new Date(),
      solicitudId,
      detalle: Object.keys(data).map((campoId: any) => ({
        campoId: +campoId,
        valor: data[campoId] ? `${data[campoId]}` : null,
      })),
    };
    createRequest(newData)
      .unwrap()
      .catch((error: any) => {
        const message = error?.data?.message ?? admissionsMadePage?.create_error_message ?? "Error al crear la solicitud";
        toast.error(message, toastErrorStyle);
      });
  };
  const changeRequestState = React.useCallback(
    (record: any) => {
      modal.confirm({
        title: admissionsMadePage?.anulate_button || "Anular solicitud",
        icon: <Icon name="" color="#FFC53D" />,
        content: admissionsMadePage?.message || `${record.solicitudNombre}?`,
        okText: admissionsMadePage?.acept_button || "Aceptar",
        onOk: () => {
          deleteRequest(record.estudianteSolicitudId);
        },
        cancelText: admissionsMadePage?.cancel_button || "Cancelar",
        style: { top: 200 },
      });
    },
    [deleteRequest, modal, admissionsMadePage],
  );

  const columns = React.useMemo<ColumnsType<ISolicitud>>(
    () => [
      {
        title: admissionsMadePage?.admission_table[0]?.label || "Fecha",
        dataIndex: "fecha",
        render: (value) => formatDate(value),
      },
      {
        title: admissionsMadePage?.admission_table[1]?.label || "Solicitud",
        dataIndex: "solicitudNombre",
        render: (text: string) => translatedData.solicitudes[text] || text,
      },
      {
        title: admissionsMadePage?.admission_table[2]?.label || "Estado",
        dataIndex: "estadoNombre",
        render: (text: string) => translatedData.estados[text] || text,
      },
      {
        title: "",
        dataIndex: "actions",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Dropdown
            overlayClassName="table-dropdown"
            className="cursor-pointer"
            key={record.solicitudId}
            trigger={["click"]}
            align={{ offset: [-40, 4] }}
            menu={{
              items: [
                {
                  key: "2",
                  label: admissionsMadePage?.anulate_button || "Anular solicitud",
                  icon: <Icon name={record.estadoId === 2 ? "" : ""} />,
                  onClick: () => changeRequestState(record),
                },
              ],
            }}
          >
            <Icon name="" isTouchable />
          </Dropdown>
        ),
      },
    ],
    [changeRequestState, admissionsMadePage, translatedData],
  );

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setAdmissionsMadePage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    getAdmissionsMadePage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setAdmissionsMadePage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setAdmissionsMadePage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      const hasData = data?.data?.result && Array.isArray(data.data.result) && data.data.result.length > 0;
      const hasConceptos = conceptos?.data && Array.isArray(conceptos.data) && conceptos.data.length > 0;
      const hasRequestById = requestById?.data?.campos && Array.isArray(requestById.data.campos) && requestById.data.campos.length > 0;

      if (!hasData && !hasConceptos && !hasRequestById) {
        setTranslatedData({
          solicitudes: {},
          estados: {},
          campos: {},
          informacionesAdicionales: {},
          descripciones: {},
          opciones: {},
        });
        return;
      }

      const solicitudesTranslations: { [key: string]: string | null } = {};
      const estadosTranslations: { [key: string]: string | null } = {};
      const camposTranslations: { [key: string]: string | null } = {};
      const informacionesAdicionalesTranslations: { [key: string]: string | null } = {};
      const descripcionesTranslations: { [key: string]: string | null } = {};
      const opcionesTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      // Traducir datos de la tabla
      if (hasData) {
        data.data.result.forEach((record: ISolicitud) => {
          if (record.solicitudNombre && !solicitudesTranslations.hasOwnProperty(record.solicitudNombre)) {
            solicitudesTranslations[record.solicitudNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, record.solicitudNombre, locale).then((translated) => {
                solicitudesTranslations[record.solicitudNombre] = translated;
              })
            );
          }
          if (record.estadoNombre && !estadosTranslations.hasOwnProperty(record.estadoNombre)) {
            estadosTranslations[record.estadoNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, record.estadoNombre, locale).then((translated) => {
                estadosTranslations[record.estadoNombre] = translated;
              })
            );
          }
        });
      }

      // Traducir conceptos del Select
      if (hasConceptos) {
        conceptos.data.forEach((concepto: any) => {
          if (concepto.solicitudNombre && !solicitudesTranslations.hasOwnProperty(concepto.solicitudNombre)) {
            solicitudesTranslations[concepto.solicitudNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, concepto.solicitudNombre, locale).then((translated) => {
                solicitudesTranslations[concepto.solicitudNombre] = translated;
              })
            );
          }
        });
      }

      // Traducir campos de la plantilla dinámica
      if (hasRequestById) {
        requestById.data.campos.forEach((campo: any) => {
          if (campo.campoNombre && !camposTranslations.hasOwnProperty(campo.campoNombre)) {
            camposTranslations[campo.campoNombre] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, campo.campoNombre, locale).then((translated) => {
                camposTranslations[campo.campoNombre] = translated;
              })
            );
          }
          if (campo.informacionAdicional && !informacionesAdicionalesTranslations.hasOwnProperty(campo.informacionAdicional)) {
            informacionesAdicionalesTranslations[campo.informacionAdicional] = null;
            translationPromises.push(
              _translateValue(SCREEN_NAME, campo.informacionAdicional, locale).then((translated) => {
                informacionesAdicionalesTranslations[campo.informacionAdicional] = translated;
              })
            );
          }
          // Traducir opciones de los selects
          // if (campo.opciones && Array.isArray(campo.opciones)) {
          //   campo.opciones.forEach((op: any) => {
          //     if (op.opcion && !opcionesTranslations.hasOwnProperty(op.opcion)) {
          //       opcionesTranslations[op.opcion] = null;
          //       translationPromises.push(
          //         _translateValue(SCREEN_NAME, op.opcion, locale).then((translated) => {
          //           opcionesTranslations[op.opcion] = translated;
          //         })
          //       );
          //     }
          //   });
          // }
        });
        // Traducir descripción
        if (requestById.data.descripcion && !descripcionesTranslations.hasOwnProperty(requestById.data.descripcion)) {
          descripcionesTranslations[requestById.data.descripcion] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, requestById.data.descripcion, locale).then((translated) => {
              descripcionesTranslations[requestById.data.descripcion] = translated;
            })
          );
        }
      }

      await Promise.all(translationPromises);
      setTranslatedData({
        solicitudes: solicitudesTranslations as { [key: string]: string },
        estados: estadosTranslations as { [key: string]: string },
        campos: camposTranslations as { [key: string]: string },
        informacionesAdicionales: informacionesAdicionalesTranslations as { [key: string]: string },
        descripciones: descripcionesTranslations as { [key: string]: string },
        opciones: opcionesTranslations as { [key: string]: string },
      });
    };
    translateData();
  }, [data?.data?.result, conceptos?.data, requestById?.data, locale]);

  if (isLoadingContent) {
    return (
      <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
        <Flex style={{ width: "100%", justifyContent: "space-between", marginBottom: "16px" }}>
          <Skeleton.Input active size="large" style={{ width: 300 }} />
          <Skeleton.Input active size="default" style={{ width: 150 }} />
        </Flex>
        <Card bodyStyle={{ padding: 0, width: "100%" }}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </Flex>
    );
  }
  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setAdmissionsMadePage(null);
      }}
    />;
  }

  return (
    <Spin spinning={isLoadingCreate} size="large" tip={admissionsMadePage?.loading_subjects_tip || "...Cargando asignaturas"}>
      <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
        <Flex style={{ width: "100%", justifyContent: "space-between" }}>
          <LargeHeadingStyled>
            {admissionsMadePage?.title || "Solicitudes realizadas"}
          </LargeHeadingStyled>

          <Button
            title=""
            type="primary"
            onClick={() => {
              setDrawerOpen(true);
              form.resetFields();
            }}
          >
            {admissionsMadePage?.newAdmission_button || "Nueva solicitud"}
          </Button>
        </Flex>
        <Content style={{ marginTop: "16px" }}>
          <Card bodyStyle={{ padding: 0, width: "100%" }}>
            <Table<ISolicitud>
              locale={{
                emptyText: (
                  <Box>
                    <Box>
                      <SmallHeadingStyled>{admissionsMadePage?.empty_message || "No se encontraron solicitudes"}</SmallHeadingStyled>
                      <Image
                        src={NoResultsSVG}
                        preview={false}
                        alt={admissionsMadePage?.empty_illustration_alt || "Ilustración sin resultados"}
                        width={200}
                      />
                    </Box>
                  </Box>
                ),
              }}
              loading={isLoading || isLoadingCreate || isLoadingDelete}
              columns={columns}
              dataSource={data?.data?.result}
              rowKey={(v) => `${v.estudianteSolicitudId}`}
              pagination={{
                onChange(page, pageSize) {
                  setPageNumber(page);
                  setRowsCount(pageSize);
                },
                pageSize: rowsCount,
                current: pageNumber,
                total: data?.data?.pageCount * rowsCount,
                showSizeChanger: true,
                style: { marginRight: "30px" },
              }}
              scroll={{ x: 800 }}
            />
            <Drawer
              title={admissionsMadePage?.titleT || "Nueva solicitud"}
              placement="right"
              open={drawerOpen}
              onClose={() => {
                setDrawerOpen(false);
              }}
            >
              {" "}
              <Form
                form={form}
                layout="vertical"
                onFinish={(data) => {
                  setDrawerOpen(false);
                  submit(data);
                }}
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <Form.Item
                  name="solicitudId"
                  label={admissionsMadePage?.request || "Solicitud a realizar"}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: admissionsMadePage?.required_field_message || "Este campo es obligatorio.",
                    },
                  ]}
                >
                  <Select
                    allowClear
                    size="large"
                    placeholder={admissionsMadePage?.select_option || "Seleccione un tipo de solicitud"}
                    options={solicitudesConceptos}
                    onChange={(e) => {
                      getRequestById(e);
                    }}
                  />
                </Form.Item>
                {requestById?.data?.requierePago ? (
                  <Form.Item name="cantidad" label={admissionsMadePage?.quantity_label || "Cantidad"} style={{ marginBottom: 4 }} required>
                    <InputNumber
                      placeholder={admissionsMadePage?.quantity_label || "Cantidad"}
                      min="1"
                      defaultValue="1"
                      max="100"
                      controls={false}
                    />
                  </Form.Item>
                ) : null}
                {requestById?.data !== undefined ? (
                  <>
                    {requestById?.data?.campos?.map((campo: any, index: number) => (
                      <div key={`${campo.tipoCampoId} ${index}`}>
                        <FormItem
                          rules={[
                            {
                              required: campo.esObligatorio,
                              message: campo.esObligatorio ? "Este campo es requerido" : "",
                            },
                          ]}
                          label={translatedData.campos[campo.campoNombre] || campo.campoNombre}
                          name={campo.campoId}
                          help={translatedData.informacionesAdicionales[campo.informacionAdicional] || campo.informacionAdicional}
                        >
                          <ConfigurationInput
                            data={{
                              id: campo.campoId,
                              label: translatedData.campos[campo.campoNombre] || campo.campoNombre,
                              nombre: campo.campoNombre,
                              opciones: campo.opciones?.map((op: any) => ({
                                opcion: op.opcion,//translatedData.opciones[op.opcion] || 
                                opcionId: op.opcionId,
                              })),
                              tipo: tiposCampos?.data?.find(
                                (tipo: any) => tipo.tipoCampoId === campo.tipoCampoId,
                              ).tipoCampoNombre,
                              descripcion: translatedData.informacionesAdicionales[campo.informacionAdicional] || campo.informacionAdicional,
                              valor: null,
                            }}
                            onChange={(value) => form.setFieldValue(campo.campoNombre, value)}
                          />
                        </FormItem>
                      </div>
                    ))}
                    <Text>{translatedData.descripciones[requestById?.data?.descripcion] || requestById?.data?.descripcion}</Text>
                  </>
                ) : null}

                {isLoadingMessage ? (
                  <Spin spinning />
                ) : (
                  <SmallMessageStyled>
                    <SmallHeadingStyled>{admissionsMadePage?.subtitle || "INFORMACIÓN IMPORTANTE"}</SmallHeadingStyled>
                    <div>
                      {locale === 'en' 
                        ? parse(requestMessage?.data?.split('-')[1] || requestMessage?.data?.split('-')[0] || '')
                        : parse(requestMessage?.data?.split('-')[0] || '')}
                    </div>
                  </SmallMessageStyled>
                )}

                <Button
                  type="primary"
                  size="large"
                  loading={isLoadingById}
                  disabled={isLoadingById}
                  htmlType="submit"
                  style={{ alignSelf: "flex-end" }}
                >
                  {admissionsMadePage?.save_button || "Enviar solicitud"}
                </Button>
              </Form>
            </Drawer>
          </Card>
        </Content>
      </Flex>
      {contextHolder}
    </Spin>
  );
};

export default Requests;
